import { FC } from "react";
import { useMutations } from "@/modules/identity-and-access/companies/services/requests";
import { useAppStore } from "@/store/appStore";
import { useCaptureStore } from "@/store/captureStore";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Icon, tokens, useDialog } from "@vintecc/components";
import { CompanySelectPanel } from "@/components/Topbar/company-panel/CompanySelectPanel";
import { useCommonTranslation } from "@/services/culture/common.translations";

import { faBuilding, faChevronDown } from "@vintecc/pro-light-svg-icons";

const CompanySelect: FC<{ showCompanyName?: boolean }> = ({ showCompanyName }) => {
  const { currentCompany, switchCompany, setCurrentCompany, refreshLoginAnswer, setIsSwitchingCompanyView, isSwitchingCompanyView } = useAppStore();
  const selectCompanyPanel = useDialog(CompanySelectPanel);
  const { switchCaptureCompany } = useMutations();
  const { setCompany } = useCaptureStore();
  const { t: commonT } = useCommonTranslation();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const companyPanelId = "topbar-company-select-panel";

  const currentCompanyName = currentCompany || commonT("common.company");

  const handleSelectCompanyClick = () => {
    if (isSwitchingCompanyView) {
      return;
    }

    selectCompanyPanel.open({
      onClose: () => selectCompanyPanel.close(),
      panelId: companyPanelId,
      onSelect: async (companyName) => {
        selectCompanyPanel.close();
        setIsSwitchingCompanyView(true);
        await switchCaptureCompany.mutateAsync(companyName);
        await refreshLoginAnswer();
        await switchCompany(companyName, navigate, routerLocation);
        setCurrentCompany(companyName);
        setCompany(companyName);
        setIsSwitchingCompanyView(false);
      },
    });
  };

  return (
    <button
      data-testid="company-select-button"
      type="button"
      className="flex items-center gap-2 h-11 py-3 px-0.75 md:px-0 cursor-pointer"
      onClick={handleSelectCompanyClick}
      aria-label={`${commonT("common.selectCompany")}. ${commonT("common.company")}: ${currentCompanyName}`}
      aria-haspopup="dialog"
      aria-controls={companyPanelId}
      aria-busy={isSwitchingCompanyView}
      disabled={isSwitchingCompanyView}
      title={commonT("common.selectCompany")}
      data-current-company={currentCompanyName}
    >
      <Icon color={tokens.color.grey[400]} icon={faBuilding} size="lg" />
      {showCompanyName && <p>{currentCompany ?? "Company"}</p>}
      <Icon color={tokens.color.grey[400]} icon={faChevronDown} size="lg" />
    </button>
  );
};

export default CompanySelect;
